import { MessageDetail, MessageDetails } from '../../message-detail.model';
import { LogService } from '../../../log.service';

import { Subject } from "rxjs";

export enum DeleteDetailState {
    Waiting,    // delete is wating to be processed
    InProgress, // delete is in progress
    Done,       // the delete was successful
    Canceled,   // the delete was canceled
    Error       // an error occured deleting
};

export const DeleteStateText: string[] = [
    "Waiting",
    "In Progress",
    "Done",
    "Canceled",
    "Error"
];

/**
 * Icons that represent that message type
 */
export const DeleteDetailTypeIcons: string[] = [
    "assets/images/dismiss24.svg",              // Waiting
    "",                                         // InProgress
    "assets/images/status_complete24.svg",      // Done
    "",                                         // Canceled
    "assets/images/formfield_error.svg",        // Error
];

export class DeleteMessageDetail extends MessageDetail {
    state: DeleteDetailState = DeleteDetailState.Waiting;
    cancel: boolean = false;
    canceled = new Subject<string>();

    constructor(uniqueId: string, message: string, icon: string, state: DeleteDetailState) {
        super(uniqueId, message, icon);
        this.state = state;
        this.statusText = DeleteStateText[state];
    }

    stop() {
        this.cancel = true;
        this.state = DeleteDetailState.Canceled;
        this.statusText = DeleteStateText[DeleteDetailState.Canceled];
        this.canceled.next(this.uniqueId);
    }
}

export class DeleteMessageDetails extends MessageDetails {
    deletingCount: number = 0;
    canceled: number = 0;
    deletingCompleted = 0;
    deletePercentComplete = 0;
    deleteErrors: number = 0;
    details: DeleteMessageDetail[] = [];

    stopDeleteRequested: boolean = false;
    stopDeletes = new Subject<boolean>();

    constructor(private logService: LogService, deleteApiInprogress: Subject<string>) {
        super();
        deleteApiInprogress.subscribe(uniqueId => {
            this.logService.logMessage(`DeleteMessageDetails:deleteApiInprogress enter : ${uniqueId}`);
            const dd = this.find(uniqueId);
            if (dd) {
                dd.state = DeleteDetailState.InProgress;
            }
        });
    }

    /**
     * Add a item to delete
     * @param detail delete detail to add
     */
    addItem(detail: DeleteMessageDetail) {
        this.logService.logMessage(`DeleteMessageDetails:addItem enter : ${detail.uniqueId}`);
        this.details.push(detail);
        this.deletingCount = this.deletingCount + 1;

        // item may already be canceled
        if (detail.state == DeleteDetailState.Canceled) {
            this.canceled++;
            this.deletingCount--;
        }

        detail.canceled.subscribe(() => {
            this.canceled++;
            this.deletingCount--;
        });
    }

    /**
     * Find a detail entry based on its document unique identifier
     * @param uniqueId Unique index for document to find
     * @return Detail item 
     */
    find(uniqueId: string) {
        return this.details.find(d => d.uniqueId == uniqueId);
    }

    /**
     * Stop all deletes that are waiting to be processed
     */
    stopAll() {
        this.logService.logMessage('DeleteMessageDetails:stopAll enter.');
        this.details.forEach(dd => {
            if (dd.state == DeleteDetailState.Waiting) {
                this.logService.logMessage('DeleteMessageDetails:stopAll : Canceling : ' + dd.uniqueId);
                this.deletingCount = this.deletingCount - 1;
                dd.stop();
            }
        });

        this.stopDeleteRequested = true;
    }

    filter(uniqueId: string): boolean {
        const dd = this.find(uniqueId);
        if (dd) {
            return dd.state == DeleteDetailState.Waiting || dd.state == DeleteDetailState.InProgress;
        }
        return false;
    }

    isComplete(): boolean {
        return this.details.findIndex(d => d.state == DeleteDetailState.Waiting || d.state == DeleteDetailState.InProgress) < 0;
    }

    markAsComplete(uniqueId: string) {
        const detail = this.find(uniqueId);
        if (detail) {
            detail.state = DeleteDetailState.Done;
            detail.statusText = DeleteStateText[DeleteDetailState.Done];
        }

        this.deletingCompleted++;
        if (this.deletingCompleted > 0) {
            this.deletePercentComplete = (100 / this.deletingCount) * this.deletingCompleted;
        }
        else {
            this.deletePercentComplete = 0;
        }

        this.emitStop();
    }

    markAsError(uniqueId: string, errorMsg: string) {
        this.logService.logMessage(errorMsg);

        const detail = this.find(uniqueId);
        if (detail) {
            this.deleteErrors++;
            detail.state = DeleteDetailState.Error;
            detail.statusText = errorMsg;
        }

        this.emitStop();
    }

    private emitStop() {
        if (this.stopDeleteRequested) {
            this.logService.logMessage('DeleteMessageDetails; emitting stop');
            this.stopDeletes.next(true);
        }
    }
}